import { useDateTimeDisplay } from '../../../platform/util/useDateTimeDisplay';
import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import AutoGraphRoundedIcon from "@mui/icons-material/AutoGraphRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useApolloClient, useQuery } from "@apollo/client";

import useLoadReduxStoreData from "../../../platform/redux/reducers/loadReduxStoreData";
import { BusinessQueryNames, GET_BOOKINGS } from "../../../platform/graphql/queries";
import { useSelectBusinesses, useSelectedBusiness } from "../../../platform/redux/store";
import { setSelectedBusiness } from "../../../platform/redux/actions/businessActions";
import { GET_BILLING_SUBSCRIPTION, GET_PLANS } from "../../../platform/graphql/queries";
import { hasStandaloneBookingAccess } from "../../../core/business/productAccess";
import { UserContext } from "../../../platform/components/auth/auth-context";
import { BookingTypeStatus, getEffectivePlan } from "../../../platform/util/utils";

const DAY_MS = 24 * 60 * 60 * 1000;

function parseJsonObject(value: any) {
  if (!value) return {};
  if (typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value !== "string") return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch (_error) {
    return {};
  }
}

function getPlanTierCode(rawCode: any) {
  const code = String(rawCode || "").trim().toUpperCase();
  if (code.startsWith("BOOKING_")) return code.replace("BOOKING_", "");
  if (code.startsWith("CARE_")) return code.replace("CARE_", "");
  return code;
}

function inferPlanProductKey(plan: any) {
  const capabilities = parseJsonObject(plan?.capabilities);
  const explicit = String(
    capabilities?.productKey || capabilities?.product?.key || capabilities?.billing?.productKey || ""
  )
    .trim()
    .toLowerCase();
  if (explicit) return explicit;

  const code = String(plan?.code || "").trim().toUpperCase();
  if (code.startsWith("BOOKING_")) return "booking";
  if (code.startsWith("CARE_")) return "care";
  return "";
}

function isBookingBusiness(business: any) {
  const plans = Array.isArray(business?.plans) ? business.plans : [];
  const hasBookingPlan = plans.some((plan: any) => inferPlanProductKey(plan) === "booking");
  const bookingTypes = Array.isArray(business?.businessBookingTypes) ? business.businessBookingTypes : [];
  return hasBookingPlan || bookingTypes.length > 0 || business?.businessBasic?.displayOnBookingPage === true;
}

function normalizeDate(value: any) {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatBookingDateTime(value: any) {
  const parsed = normalizeDate(value);
  if (!parsed) return "Unknown time";
  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function isDeletedBooking(status: any) {
  return Number(status) === Number(BookingTypeStatus.DELETED);
}

function getBookingLifecycleLabel(booking: any, now = new Date()) {
  if (isDeletedBooking(booking?.status)) return "Canceled";
  const when = normalizeDate(booking?.time);
  if (!when) return "Scheduled";
  return when < now ? "Completed" : "Upcoming";
}

function expandWorkspaceBookings(rawBookings: any[], business: any) {
  const businessId = Number(business?.businessBasic?.id || 0);
  const businessName = String(business?.businessBasic?.name || "Business").trim();

  return (Array.isArray(rawBookings) ? rawBookings : []).flatMap((booking: any) => {
    const recurringOccurrences = Array.isArray(booking?.recurringOccurrences)
      ? booking.recurringOccurrences
      : [];
    const occurrenceTimes = recurringOccurrences.length > 0 ? recurringOccurrences : [booking?.time];

    return occurrenceTimes
      .map((timeValue: any, index: number) => {
        const parsedTime = normalizeDate(timeValue);
        if (!parsedTime) return null;
        return {
          ...booking,
          id: recurringOccurrences.length > 0 ? `${booking?.id || "booking"}:${index}` : booking?.id,
          parentBookingId: booking?.id,
          businessId,
          businessName,
          time: parsedTime.toISOString(),
          whenMs: parsedTime.getTime(),
          lifecycle: getBookingLifecycleLabel({ ...booking, time: parsedTime.toISOString() }),
        };
      })
      .filter(Boolean);
  });
}

function downloadBookingCsv(rows: any[]) {
  if (!rows.length || typeof window === "undefined") return;
  const escapeCell = (value: any) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  const header = [
    "Business",
    "Booking Type",
    "Invitee",
    "Staff",
    "Time",
    "Status",
    "Email",
    "Phone",
  ];
  const lines = [
    header.map(escapeCell).join(","),
    ...rows.map((row) =>
      [
        row.businessName,
        row.bookingTypeName,
        row.inviteeName,
        row.staffName,
        formatBookingDateTime(row.time),
        row.lifecycle,
        row.inviteeEmail,
        Array.isArray(row.inviteePhoneNumbers) ? row.inviteePhoneNumbers.join(" | ") : "",
      ]
        .map(escapeCell)
        .join(","),
    ),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `booking-premium-report-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
}

function getLifecycleChipProps(lifecycle: string) {
  if (lifecycle === "Upcoming") {
    return { label: "Upcoming", sx: { bgcolor: "action.selected", color: "success.main" } };
  }
  if (lifecycle === "Completed") {
    return { label: "Completed", sx: { bgcolor: "action.selected", color: "info.main" } };
  }
  if (lifecycle === "Canceled") {
    return { label: "Canceled", sx: { bgcolor: "action.selected", color: "error.main" } };
  }
  return { label: lifecycle || "Scheduled", sx: { bgcolor: "action.selected", color: "text.primary" } };
}

function getStaffStatusChipProps(status: string) {
  if (status === "Active") {
    return { label: "Active", sx: { bgcolor: "action.selected", color: "success.main" } };
  }
  return { label: status || "Pending", sx: { bgcolor: "action.selected", color: "warning.main" } };
}

function SectionShell({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        overflow: "hidden",
        backgroundColor: "background.paper",
      }}
    >
      <Box
        sx={{
          px: 2.25,
          py: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "flex-start" }}
        >
          <Box>
            <Typography variant="h6" fontWeight={800}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {description}
            </Typography>
          </Box>
          {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
        </Stack>
      </Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Paper>
  );
}

function MetricCard({
  icon,
  label,
  value,
  accent,
  helperText,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  accent: string;
  helperText: string;
}): JSX.Element {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: "1px solid",
        borderColor: alpha(accent, 0.22),
        background: (theme) => `linear-gradient(180deg, ${alpha(accent, 0.12)} 0%, ${theme.palette.background.paper} 100%)`,
        minHeight: 132,
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2.5,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(accent, 0.14),
              color: accent,
            }}
          >
            {icon}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
        </Stack>
        <Typography variant="h4" fontWeight={800} lineHeight={1}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {helperText}
        </Typography>
      </Stack>
    </Paper>
  );
}

export default function BookingPremiumWorkspacePanel(): JSX.Element | null {
  const { dateTimeLabel: formatBookingDateTime } = useDateTimeDisplay();
  useLoadReduxStoreData(BusinessQueryNames.GET_MY_BUSINESSES);

  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { providerUser } = React.useContext(UserContext);
  const businesses = useSelectBusinesses();
  const selectedBusiness = useSelectedBusiness();
  const businessId = Number(selectedBusiness?.businessBasic?.id || providerUser?.defaultBusinessId || 0) || null;

  const { data: subscriptionData } = useQuery(GET_BILLING_SUBSCRIPTION, {
    variables: { businessId, productKey: "booking" },
    skip: !businessId,
    fetchPolicy: "network-only",
  });
  const { data: plansData } = useQuery(GET_PLANS, {
    variables: { product: "booking" },
  });

  const subscription = subscriptionData?.getBillingSubscription;
  const currentPlan = (plansData?.plans || []).find(
    (plan: any) => String(plan?.id) === String(subscription?.planId)
  );
  const currentTierCode = getPlanTierCode(currentPlan?.code);
  const effectiveBusinessPlan = Number(getEffectivePlan(providerUser) || 0);
  const isPremiumBooking =
    currentTierCode === "PREMIUM" ||
    currentTierCode === "ENTERPRISE" ||
    effectiveBusinessPlan >= 3;

  const canUseStandaloneBooking = hasStandaloneBookingAccess(providerUser);
  const bookingBusinesses = React.useMemo(
    () => (Array.isArray(businesses) ? businesses.filter(isBookingBusiness) : []),
    [businesses]
  );
  const bookingYear = React.useMemo(() => new Date().getFullYear(), []);
  const [workspaceBookings, setWorkspaceBookings] = React.useState<any[]>([]);
  const [workspaceLoading, setWorkspaceLoading] = React.useState(false);
  const [workspaceError, setWorkspaceError] = React.useState<string | null>(null);

  const crossBusinessStaffRows = React.useMemo(() => {
    return bookingBusinesses.flatMap((business: any) => {
      const businessName = business?.businessBasic?.name || "Business";
      return (business?.businessStaffs || []).map((staff: any) => ({
        businessId: Number(business?.businessBasic?.id || 0),
        businessName,
        staffId: Number(staff?.id || 0),
        name:
          String(staff?.user?.username || "").trim() ||
          String(staff?.initials || "").trim() ||
          `Staff #${staff?.id || "-"}`,
        roleName: String(staff?.role?.name || "Staff").trim(),
        status: Number(staff?.status || 0) === 1 ? "Active" : "Pending",
        bookingTypeCount: Array.isArray(staff?.bookingTypes) ? staff.bookingTypes.length : 0,
      }));
    });
  }, [bookingBusinesses]);

  const totalBookingTypes = React.useMemo(
    () =>
      bookingBusinesses.reduce(
        (sum: number, business: any) => sum + (Array.isArray(business?.businessBookingTypes) ? business.businessBookingTypes.length : 0),
        0
      ),
    [bookingBusinesses]
  );

  const activeStaffCount = React.useMemo(
    () => crossBusinessStaffRows.filter((staff) => staff.status === "Active").length,
    [crossBusinessStaffRows]
  );
  const selectedBusinessName = String(selectedBusiness?.businessBasic?.name || "Current business").trim();

  const loadWorkspaceBookings = React.useCallback(async () => {
    if (!canUseStandaloneBooking || !isPremiumBooking || bookingBusinesses.length === 0) {
      setWorkspaceLoading(false);
      setWorkspaceBookings([]);
      setWorkspaceError(null);
      return;
    }

    setWorkspaceLoading(true);
    setWorkspaceError(null);

    const results = await Promise.allSettled(
      bookingBusinesses.map(async (business: any) => {
        const nextBusinessId = Number(business?.businessBasic?.id || 0);
        if (!nextBusinessId) return [];
        const response = await apolloClient.query({
          query: GET_BOOKINGS,
          variables: {
            businessId: nextBusinessId,
            selectedYear: bookingYear,
          },
          fetchPolicy: "network-only",
        });
        return expandWorkspaceBookings(response?.data?.getBookings || [], business);
      }),
    );

    const nextRows: any[] = [];
    const failedBusinesses: string[] = [];
    results.forEach((result, index) => {
      if (result.status === "fulfilled") {
        nextRows.push(...result.value);
        return;
      }
      failedBusinesses.push(String(bookingBusinesses[index]?.businessBasic?.name || "Business"));
    });

    nextRows.sort((left, right) => Number(left?.whenMs || 0) - Number(right?.whenMs || 0));
    setWorkspaceBookings(nextRows);
    setWorkspaceError(
      failedBusinesses.length
        ? `Some booking workspaces could not load: ${failedBusinesses.join(", ")}`
        : null,
    );
    setWorkspaceLoading(false);
  }, [apolloClient, bookingBusinesses, bookingYear, canUseStandaloneBooking, isPremiumBooking]);

  React.useEffect(() => {
    loadWorkspaceBookings();
  }, [loadWorkspaceBookings]);

  const workspaceMetrics = React.useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfTomorrow = new Date(startOfToday.getTime() + DAY_MS);
    const nextSevenDays = new Date(startOfToday.getTime() + DAY_MS * 7);

    const activeRows = workspaceBookings.filter((row) => !isDeletedBooking(row?.status));
    const upcomingRows = activeRows.filter((row) => Number(row?.whenMs || 0) >= now.getTime());
    const todayRows = upcomingRows.filter((row) => Number(row?.whenMs || 0) < startOfTomorrow.getTime());
    const nextSevenRows = upcomingRows.filter((row) => Number(row?.whenMs || 0) < nextSevenDays.getTime());

    const byBusiness = bookingBusinesses
      .map((business: any) => {
        const nextBusinessId = Number(business?.businessBasic?.id || 0);
        const rows = activeRows.filter((row) => Number(row?.businessId || 0) === nextBusinessId);
        const upcoming = rows.filter((row) => Number(row?.whenMs || 0) >= now.getTime());
        return {
          businessId: nextBusinessId,
          businessName: String(business?.businessBasic?.name || "Business").trim(),
          totalBookings: rows.length,
          upcomingBookings: upcoming.length,
          activeStaff: (business?.businessStaffs || []).filter((staff: any) => Number(staff?.status || 0) === 1).length,
          bookingTypes: Array.isArray(business?.businessBookingTypes) ? business.businessBookingTypes.length : 0,
        };
      })
      .sort((left, right) => right.totalBookings - left.totalBookings);

    return {
      upcomingCount: upcomingRows.length,
      todayCount: todayRows.length,
      nextSevenCount: nextSevenRows.length,
      activeRows,
      upcomingRows,
      byBusiness,
    };
  }, [bookingBusinesses, workspaceBookings]);
  const featuredUpcomingBooking = workspaceMetrics.upcomingRows[0] || null;
  const topBusinessRows = workspaceMetrics.byBusiness.slice(0, 4);
  const premiumHighlights = [
    "See staff availability across subscribed booking businesses",
    "Use round-robin host assignment when guests pick All Staff",
    "Export booking reports and track volume from one place",
    "Jump into staff, calendar, or schedule tools without losing context",
  ];

  const handleOpenBusiness = (targetBusinessId: number, path: string) => {
    if (!targetBusinessId) return;
    dispatch(setSelectedBusiness(targetBusinessId));
    try {
      localStorage.setItem("selectedBusinessId", String(targetBusinessId));
    } catch (_error) {
      // ignore storage failures
    }
    navigate(path);
  };

  if (!canUseStandaloneBooking || !isPremiumBooking) {
    return null;
  }

  return (
    <Stack spacing={2} sx={{ mb: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: "1px solid",
          borderColor: alpha("#0f766e", 0.18),
          bgcolor: "background.paper",
          position: "relative",
          overflow: "hidden",
          "&::after": {
            content: '""',
            position: "absolute",
            width: 220,
            height: 220,
            borderRadius: "50%",
            background: alpha("#0f766e", 0.08),
            top: -70,
            right: -50,
            pointerEvents: "none",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1.3fr 0.95fr" },
            gap: 2,
          }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 800 }}>
                Booking Premium
              </Typography>
              <Typography variant="h4" fontWeight={900} sx={{ mt: 0.25, letterSpacing: -0.4 }}>
                Run every booking business from one premium workspace
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                See staff coverage, monitor bookings across businesses, rotate hosts fairly,
                and move from analytics into action without leaving the Booking dashboard.
              </Typography>
            </Box>

            <Stack direction={{ xs: "column", md: "row" }} spacing={1.1} useFlexGap flexWrap="wrap">
              <Chip icon={<ApartmentRoundedIcon />} label={`${bookingBusinesses.length} booking businesses`} />
              <Chip icon={<GroupsOutlinedIcon />} label={`${activeStaffCount} active staff visible`} />
              <Chip icon={<CalendarMonthOutlinedIcon />} label={`${totalBookingTypes} booking types across businesses`} />
              <Chip icon={<AutoGraphRoundedIcon />} label={`${workspaceMetrics.upcomingCount} upcoming bookings`} />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} useFlexGap flexWrap="wrap">
              <Button variant="contained" onClick={() => navigate("/myschedule")}>
                Open Team Availability
              </Button>
              <Button variant="outlined" onClick={() => navigate("/biz/calendar")}>
                Open Booking Calendar
              </Button>
              <Button variant="outlined" onClick={() => navigate("/biz/staff")}>
                Open Staff Workspace
              </Button>
              <Button variant="outlined" onClick={() => navigate("/biz/business/6?v=booking")}>
                Roles & Permissions
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadRoundedIcon />}
                onClick={() => downloadBookingCsv(workspaceMetrics.activeRows)}
                disabled={!workspaceMetrics.activeRows.length}
              >
                Export Report
              </Button>
            </Stack>

            <Alert severity="info" sx={{ borderRadius: 2.5 }}>
              Premium extends Booking beyond one business. You can see cross-business staff coverage,
              manage upcoming bookings centrally, and use round-robin host assignment for All Staff bookings.
            </Alert>
            {workspaceError ? <Alert severity="warning" sx={{ borderRadius: 2.5 }}>{workspaceError}</Alert> : null}
          </Stack>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: "1px solid",
              borderColor: alpha("#0f766e", 0.18),
              bgcolor: "background.paper",
              backdropFilter: "blur(8px)",
            }}
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" fontWeight={800}>
                  What Premium unlocks right now
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Built into the Booking dashboard, not tucked away behind separate tools.
                </Typography>
              </Box>

              <Stack spacing={1}>
                {premiumHighlights.map((item) => (
                  <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                    <CheckCircleRoundedIcon sx={{ fontSize: 18, mt: 0.15, color: "#0f766e" }} />
                    <Typography variant="body2">{item}</Typography>
                  </Stack>
                ))}
              </Stack>

              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: alpha("#0f766e", 0.05),
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Current focus
                </Typography>
                <Typography variant="subtitle1" fontWeight={800} sx={{ mt: 0.4 }}>
                  {selectedBusinessName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                  {featuredUpcomingBooking
                    ? `Next booking: ${featuredUpcomingBooking.bookingTypeName || "Booking"} on ${formatBookingDateTime(featuredUpcomingBooking.time)}`
                    : "No upcoming booking is loaded yet for the current cross-business view."}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Paper>

      <SectionShell
        title="Booking Analytics And Reporting"
        description="Track booking demand across subscribed businesses, spot today's work, and export a report when you need a clean handoff."
      >
        {workspaceLoading ? (
          <Stack direction="row" spacing={1.25} alignItems="center">
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading booking analytics...
            </Typography>
          </Stack>
        ) : (
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", xl: "repeat(4, minmax(0, 1fr))" },
            }}
          >
            <MetricCard
              icon={<CalendarMonthOutlinedIcon fontSize="small" />}
              label="Upcoming"
              value={workspaceMetrics.upcomingCount}
              accent="#0f766e"
              helperText="Bookings still ahead across subscribed businesses."
            />
            <MetricCard
              icon={<AutoGraphRoundedIcon fontSize="small" />}
              label="Today"
              value={workspaceMetrics.todayCount}
              accent="#2563eb"
              helperText="Today's bookings that still need attention."
            />
            <MetricCard
              icon={<InsightsOutlinedIcon fontSize="small" />}
              label="Next 7 Days"
              value={workspaceMetrics.nextSevenCount}
              accent="#7c3aed"
              helperText="Short-range demand for staffing and follow-up."
            />
            <MetricCard
              icon={<GroupsOutlinedIcon fontSize="small" />}
              label="Active Staff"
              value={activeStaffCount}
              accent="#c2410c"
              helperText="Visible hosts and schedulers across the workspace."
            />
          </Box>
        )}
      </SectionShell>

      <SectionShell
        title="Cross-Business Staff Visibility"
        description="Review staff across booking businesses, then jump directly into schedule or staff management for the right business."
      >
        {crossBusinessStaffRows.length === 0 ? (
          <Alert severity="warning">
            No booking staff were found across your loaded businesses yet.
          </Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Business</TableCell>
                <TableCell>Staff</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Assigned Booking Types</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {crossBusinessStaffRows.slice(0, 18).map((row, index) => {
                const statusChip = getStaffStatusChipProps(row.status);
                return (
                  <TableRow
                    key={`${row.businessId}-${row.staffId}`}
                    sx={{
                      bgcolor: index % 2 === 0 ? alpha("#0f172a", 0.01) : "transparent",
                      "& td": { borderColor: alpha("#0f172a", 0.06) },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>
                        {row.businessName}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.roleName}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={statusChip.label}
                        sx={{ fontWeight: 700, ...statusChip.sx }}
                      />
                    </TableCell>
                    <TableCell>{row.bookingTypeCount}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => handleOpenBusiness(row.businessId, "/myschedule")}>
                          Schedule
                        </Button>
                        <Button size="small" onClick={() => handleOpenBusiness(row.businessId, "/biz/staff")}>
                          Staff
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </SectionShell>

      <SectionShell
        title="Centralized Booking Management"
        description="Review upcoming bookings across subscribed businesses and jump into the right calendar or schedule view without switching tabs manually."
        action={
          featuredUpcomingBooking ? (
            <Chip
              size="small"
              icon={<CalendarMonthOutlinedIcon />}
              label={`Next up: ${featuredUpcomingBooking.businessName}`}
            />
          ) : undefined
        }
      >
        <Stack spacing={2}>
          {featuredUpcomingBooking ? (
            <Paper
              elevation={0}
              sx={{
                p: 1.75,
                borderRadius: 2.5,
                border: "1px solid",
                borderColor: alpha("#0f766e", 0.16),
                bgcolor: alpha("#0f766e", 0.05),
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Next booking in the workspace
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={800} sx={{ mt: 0.35 }}>
                    {featuredUpcomingBooking.bookingTypeName || "Booking"} with{" "}
                    {featuredUpcomingBooking.inviteeName || featuredUpcomingBooking.bookedForName || "Guest"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                    {formatBookingDateTime(featuredUpcomingBooking.time)} - {featuredUpcomingBooking.businessName}
                    {featuredUpcomingBooking.staffName ? ` - ${featuredUpcomingBooking.staffName}` : ""}
                  </Typography>
                </Box>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleOpenBusiness(featuredUpcomingBooking.businessId, "/biz/calendar")}
                  >
                    Open Calendar
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleOpenBusiness(featuredUpcomingBooking.businessId, "/myschedule")}
                  >
                    Open Schedule
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          ) : null}

          {workspaceLoading ? (
            <Stack direction="row" spacing={1.25} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading centralized bookings...
              </Typography>
            </Stack>
          ) : workspaceMetrics.upcomingRows.length === 0 ? (
            <Alert severity="info">
              No upcoming bookings were found across your subscribed booking businesses.
            </Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Business</TableCell>
                  <TableCell>Booking</TableCell>
                  <TableCell>Invitee</TableCell>
                  <TableCell>Host</TableCell>
                  <TableCell>When</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workspaceMetrics.upcomingRows.slice(0, 18).map((row, index) => {
                  const lifecycleChip = getLifecycleChipProps(row.lifecycle);
                  return (
                    <TableRow
                      key={String(row.id)}
                      sx={{
                        bgcolor: index % 2 === 0 ? alpha("#0f172a", 0.01) : "transparent",
                        "& td": { borderColor: alpha("#0f172a", 0.06) },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>
                          {row.businessName}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.bookingTypeName || "Booking"}</TableCell>
                      <TableCell>{row.inviteeName || row.bookedForName || "Guest"}</TableCell>
                      <TableCell>{row.staffName || "Unassigned"}</TableCell>
                      <TableCell>{formatBookingDateTime(row.time)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={lifecycleChip.label}
                          sx={{ fontWeight: 700, ...lifecycleChip.sx }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" onClick={() => handleOpenBusiness(row.businessId, "/biz/calendar")}>
                            Calendar
                          </Button>
                          <Button size="small" onClick={() => handleOpenBusiness(row.businessId, "/myschedule")}>
                            Schedule
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Stack>
      </SectionShell>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", xl: "1.15fr 0.85fr" },
        }}
      >
        <Stack spacing={2}>
          <SectionShell
            title="Team Scheduling"
            description="Premium keeps multi-business scheduling actionable instead of forcing you to manage each business in isolation."
          >
            <Stack spacing={1.5}>
              <Paper
                variant="outlined"
                sx={{ p: 1.5, borderRadius: 2.5, bgcolor: alpha("#2563eb", 0.03) }}
              >
                <Typography variant="subtitle2" fontWeight={800}>
                  Shared availability flow
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Open My Schedule to see team availability, then switch into the right business context only when you need to make a change.
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.25 }}>
                  <Button variant="contained" size="small" onClick={() => navigate("/myschedule")}>
                    Open Team Availability
                  </Button>
                  <Button variant="outlined" size="small" onClick={() => navigate("/biz/calendar")}>
                    Open Booking Calendar
                  </Button>
                </Stack>
              </Paper>

              <Paper
                variant="outlined"
                sx={{ p: 1.5, borderRadius: 2.5, bgcolor: alpha("#0f766e", 0.03) }}
              >
                <Typography variant="subtitle2" fontWeight={800}>
                  Round-robin assignment
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Guests who pick All Staff are now routed to the next eligible host instead of always falling to the first assigned staff member.
                </Typography>
              </Paper>
            </Stack>
          </SectionShell>

          <SectionShell
            title="Business Performance"
            description="Compare booking load by business so Premium admins can spot where follow-up and staffing are needed most."
          >
            {topBusinessRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No booking business data is loaded yet.
              </Typography>
            ) : (
              <Stack spacing={1.25}>
                {topBusinessRows.map((row) => {
                  const maxBookings = Math.max(1, topBusinessRows[0]?.totalBookings || 1);
                  const bookingShare = Math.round((row.totalBookings / maxBookings) * 100);
                  return (
                    <Paper
                      key={row.businessId}
                      variant="outlined"
                      sx={{ p: 1.5, borderRadius: 2.5 }}
                    >
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                          <Typography variant="body2" fontWeight={700}>
                            {row.businessName}
                          </Typography>
                          <Chip size="small" label={`${row.upcomingBookings} upcoming`} />
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={bookingShare}
                          sx={{
                            height: 8,
                            borderRadius: 999,
                            bgcolor: alpha("#0f172a", 0.08),
                            "& .MuiLinearProgress-bar": {
                              borderRadius: 999,
                              bgcolor: "#0f766e",
                            },
                          }}
                        />
                        <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
                          <Typography variant="caption" color="text.secondary">
                            {row.totalBookings} total bookings
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.activeStaff} active staff
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.bookingTypes} booking types
                          </Typography>
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </SectionShell>
        </Stack>

        <Stack spacing={2}>
          <SectionShell
            title="Shared Admin Controls"
            description="Premium admins can move straight into staff roles and permissions without backing out of the Booking workspace."
            action={
              <Button size="small" variant="outlined" onClick={() => navigate("/biz/business/6?v=booking")}>
                Open Roles
              </Button>
            }
          >
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                Advanced host and scheduler permissions stay in the existing role builder, but this panel keeps the path visible for the business you are managing now.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button variant="contained" size="small" onClick={() => navigate("/biz/staff")}>
                  Open Staff Workspace
                </Button>
                <Button variant="outlined" size="small" onClick={() => navigate("/biz/business/6?v=booking")}>
                  Edit Roles & Permissions
                </Button>
              </Stack>
            </Stack>
          </SectionShell>

          <SectionShell
            title="Priority Support"
            description="Premium booking workspaces can jump straight into support without searching the rest of the platform."
          >
            <Stack spacing={1.25}>
              <Typography variant="body2" color="text.secondary">
                Use direct Booking Premium support when you need help with cross-business scheduling, reporting, or workspace setup.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button
                  component="a"
                  href="/contact"
                  variant="contained"
                  startIcon={<SupportAgentRoundedIcon />}
                >
                  Email Support
                </Button>
                <Button
                  component="a"
                  href="tel:+13608519658"
                  variant="outlined"
                >
                  Call Support
                </Button>
              </Stack>
            </Stack>
          </SectionShell>
        </Stack>
      </Box>
    </Stack>
  );
}
